import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchUserProfile } from "../../redux/authSlice";
import { fetchCompanyProfile } from "../../redux/companySlice";
import { COMPANY_LOGIN_PATH } from "../../config/authPaths";
import {
  dashboardPath,
  storefrontRoot,
} from "../../config/storefrontRouting";
import {
  canAccessDashboard,
  firstAccessibleDashboardPath,
} from "../../permissions/dashboardRouteAccess";

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Determine principal type from stored value or by parsing the JWT (without verification).
 * Returns 'company' | 'user' | null.
 */
function getStoredPrincipalType(): "company" | "user" | null {
  const stored = localStorage.getItem("principalType");
  if (stored === "company" || stored === "user") return stored;

  // Fallback: peek at token payload to infer type
  const token = localStorage.getItem("accessToken");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.type === "company") return "company";
    if (payload.type === "user" || payload.sub) return "user";
  } catch {
    // ignore
  }
  return null;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const dispatch = useAppDispatch();
  const company = useAppSelector((s) => s.company);
  const auth = useAppSelector((s) => s.auth);
  const location = useLocation();

  const hasToken =
    typeof localStorage !== "undefined" &&
    !!localStorage.getItem("accessToken");
  const storedType = hasToken ? getStoredPrincipalType() : null;

  const isAuthenticated = company.isAuthenticated || auth.isAuthenticated;
  const profileLoaded = !!company.id;
  const userLoaded = !!auth.user;

  // Restore session on refresh:
  // 1. Always load company profile (company name, theme, etc.) for both company and user tokens.
  // 2. If the token is a user token, also load user profile (permissions).
  useEffect(() => {
    if (!hasToken) return;

    (async () => {
      // Load company profile if not yet loaded (needed for theme, name, etc.)
      if (!profileLoaded) {
        await dispatch(fetchCompanyProfile());
      }

      // If it's a user token, load user profile to get permissions
      if (storedType === "user" && !userLoaded) {
        await dispatch(fetchUserProfile());
      }
    })();
  }, [hasToken, profileLoaded, userLoaded, storedType, dispatch]);

  // Show loading while restoring session
  // For company: show loading while company profile not loaded
  // For user: show loading while user profile not loaded (permissions needed for sidebar)
  const stillLoading =
    hasToken && (!profileLoaded || (storedType === "user" && !userLoaded));

  if (stillLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <Navigate to={COMPANY_LOGIN_PATH} state={{ from: location }} replace />
    );
  }

  // Storefront PUBLIC customers cannot use the dashboard.
  const isDashboardBlockedUser =
    storedType === "user" &&
    auth.isAuthenticated &&
    !canAccessDashboard({
      roleType: auth.user?.role?.roleType,
      permissions: auth.permissions,
    });

  if (isDashboardBlockedUser) {
    if (company.slug) {
      return <Navigate to={storefrontRoot(company.slug)} replace />;
    }
    return <Navigate to={COMPANY_LOGIN_PATH} replace />;
  }

  return <>{children}</>;
}

export function GuestGuard({ children }: AuthGuardProps) {
  const company = useAppSelector((s) => s.company);
  const auth = useAppSelector((s) => s.auth);
  const isAuthenticated = company.isAuthenticated || auth.isAuthenticated;

  if (isAuthenticated) {
    const isPublicCustomer =
      auth.principalType === "user" &&
      !canAccessDashboard({
        roleType: auth.user?.role?.roleType,
        permissions: auth.permissions,
      });
    if (isPublicCustomer && company.slug) {
      return <Navigate to={storefrontRoot(company.slug)} replace />;
    }
    return (
      <Navigate
        to={
          firstAccessibleDashboardPath({
            isCompanyPrincipal: auth.principalType === "company",
            permissions: auth.permissions,
          }) ?? dashboardPath()
        }
        replace
      />
    );
  }

  return <>{children}</>;
}
